import { Container, Divider, Stack } from "@mui/material";
import objectHash from "object-hash";
import { useCallback, useState } from "react";
import PaginationControl from "@entrilia/shared/components/PaginationControl";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import { api } from "../../api/client";
import useClientContext from "../../contexts/useClientContext";
import useClientPageTitle from "../../contexts/useClientPageTitle";
import { pageRoutes } from "../../routes";
import NewsFilters from "./NewsFilters";
import NewsPageContent from "./NewsPageContent";
import {
  initialNewsPageState,
  mapNewsPageStateToRequest,
  setCategoryAction,
  setDateRangeAction,
  setPageIndexAction,
  setSearchTermAction,
} from "./newsPageState";

const getCategories = (signal?: AbortSignal) => api.investorCommunications.getCategories(signal);

const NewsPage = () => {
  useClientPageTitle("News");

  const { clientCode } = useClientContext();
  const [state, setState] = useState(initialNewsPageState);

  const stateHash = objectHash(state);
  const searchNews = useCallback(
    (signal?: AbortSignal) => api.investorCommunications.search(mapNewsPageStateToRequest(state), signal),
    // eslint-disable-next-line @eslint-react/exhaustive-deps
    [stateHash]
  );

  const [categories, categoriesError] = useFetch(getCategories);
  const [newsPage, fetchError, { isFetching }] = useFetch(searchNews);

  if (categoriesError) {
    logError(categoriesError, "[NewsPage] getCategories");
  }

  const newsPagePath = `/${clientCode}/${pageRoutes.news}`;
  const totalPages = newsPage?.totalPages ?? 0;

  return (
    <>
      <NewsFilters
        categories={categories ?? []}
        state={state}
        onCategoryChange={(categoryExternalId) => setState(setCategoryAction(categoryExternalId))}
        onDateRangeChange={(dateRange) => setState(setDateRangeAction(dateRange))}
        onSearch={(searchTerm) => setState(setSearchTermAction(searchTerm))}
      />
      <ScrollableFlexContainer>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          <Stack spacing={3}>
            <NewsPageContent
              newsPage={newsPage}
              fetchError={fetchError}
              isFetching={isFetching}
              isFirstPage={state.pageIndex === 0}
              newsPagePath={newsPagePath}
            />
            {!fetchError && !isFetching && totalPages > 1 && (
              <>
                <Divider />
                <Stack sx={{ alignItems: "center" }}>
                  <PaginationControl
                    totalPages={totalPages}
                    pageIndex={state.pageIndex}
                    onPageIndexChange={(pageIndex) => setState(setPageIndexAction(pageIndex))}
                  />
                </Stack>
              </>
            )}
          </Stack>
        </Container>
      </ScrollableFlexContainer>
    </>
  );
};

export default NewsPage;
